// Members roster for a persona Account page (Sales / Ops / Audit) rendered in the
// shared-primitives visual idiom (Card / CardHead / Table). It fetches the org's
// members from GET /organizations/:id/members and — when the caller may mutate —
// lets an operator invite (POST), change a role (PATCH) and remove (DELETE) a
// member. Read-only personas (Audit) still see the roster but never the controls.
//
// ERROR HANDLING IS DELIBERATE (the reason this exists): a roster-fetch failure
// renders an honest in-card banner with Retry — never a silent empty list — and
// every mutation surfaces its failure through a toast. Rows the backend flags
// `degraded` (auth-side email/MFA lookup was unavailable) render defensively.
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardHead, Table, Td, Row, Btn } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from '@/components/shared/ToastProvider';
import { apiRequest } from '@/lib/supabase';
import { useCurrentUser } from '@/context/PersonaProvider';

// Presented member row (backend presentUser shape). Every field beyond id/role is
// optional so a degraded/partial row can never crash the panel.
export interface OrgMember {
  id: string;
  name?: string | null;
  full_name?: string | null;
  email?: string | null;
  role: string;
  is_admin?: boolean;
  is_active?: boolean;
  mfa_enabled?: boolean;
  degraded?: boolean;
  created_at?: string | null;
}

// Roles selectable in the member controls. super_admin is added only for a
// super_admin caller — mirrors the server-side gate in user.routes.ts.
const MEMBER_ROLE_OPTIONS: [string, string][] = [
  ['viewer', 'Read Only'],
  ['member', 'Member'],
  ['billing', 'Billing'],
  ['admin', 'Admin'],
];

const empty: React.CSSProperties = { padding: '28px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 };
const fieldStyle: React.CSSProperties = { padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--surface)' };

export function OrgMembersCard({ orgId, canMutate, accent }: { orgId: string; canMutate: boolean; accent?: string }) {
  const currentUser = useCurrentUser();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const roleOptions = isSuperAdmin
    ? [...MEMBER_ROLE_OPTIONS, ['super_admin', 'Super Admin'] as [string, string]]
    : MEMBER_ROLE_OPTIONS;

  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', role: 'member' });
  const [addingMember, setAddingMember] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState('member');
  const [removeTarget, setRemoveTarget] = useState<{ id: string; name: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await apiRequest<OrgMember[]>(`/organizations/${orgId}/members`);
      setMembers(Array.isArray(rows) ? rows : []);
    } catch (e) {
      // Honest failure — NOT a silent empty list. Surface it with a retry.
      setError(e instanceof Error ? e.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingMember(true);
    try {
      await apiRequest(`/organizations/${orgId}/members`, { method: 'POST', body: JSON.stringify(addForm) });
      toast({ kind: 'success', title: 'Invitation sent', body: `Invited ${addForm.email} to this account.` });
      setAddForm({ email: '', name: '', role: 'member' });
      setShowAdd(false);
      await load();
    } catch (err) {
      toast({ kind: 'error', title: 'Invite failed', body: err instanceof Error ? err.message : 'Could not invite member.' });
    } finally {
      setAddingMember(false);
    }
  };

  const saveRole = async (userId: string) => {
    try {
      await apiRequest(`/organizations/${orgId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role: editRole }) });
      setEditingId(null);
      await load();
    } catch (err) {
      toast({ kind: 'error', title: 'Update failed', body: err instanceof Error ? err.message : 'Could not update member role.' });
    }
  };

  const remove = async (userId: string) => {
    try {
      await apiRequest(`/organizations/${orgId}/members/${userId}`, { method: 'DELETE' });
      toast({ kind: 'success', title: 'Member removed', body: 'Their account was deactivated and detached from this org.' });
      await load();
    } catch (err) {
      toast({ kind: 'error', title: 'Remove failed', body: err instanceof Error ? err.message : 'Could not remove member.' });
    }
  };

  const cols = [
    { label: 'Member' },
    { label: 'Role' },
    { label: 'MFA' },
    { label: 'Status' },
    { label: 'Joined' },
    ...(canMutate ? [{ label: '', align: 'right' as const }] : []),
  ];

  return (
    <Card style={{ overflow: 'hidden', marginTop: 16 }}>
      <CardHead
        title={`Members${members.length ? ` (${members.length})` : ''}`}
        sub="People with access to this account · invite, change role & remove"
        right={canMutate ? (
          <Btn sm icon={showAdd ? 'x' : 'user-plus'} accent={accent} onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? 'Cancel' : 'Add member'}
          </Btn>
        ) : undefined}
      />

      {canMutate && showAdd && (
        <form onSubmit={invite} style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input type="email" required value={addForm.email} onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))} placeholder="member@company.com" style={{ ...fieldStyle, flex: '1 1 200px' }} />
          <input type="text" required value={addForm.name} onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))} placeholder="Full name" style={{ ...fieldStyle, flex: '1 1 160px' }} />
          <select value={addForm.role} onChange={(e) => setAddForm((p) => ({ ...p, role: e.target.value }))} style={{ ...fieldStyle, flex: '0 0 auto' }} aria-label="Role">
            {roleOptions.map(([r, label]) => <option key={r} value={r}>{label}</option>)}
          </select>
          <Btn sm kind="primary" accent={accent} type="submit" disabled={addingMember} icon="send">
            {addingMember ? 'Sending…' : 'Send invite'}
          </Btn>
        </form>
      )}

      {error ? (
        <div style={{ padding: 18 }}>
          <AlertBanner kind="error" action={load} actionLabel="Retry">{error}</AlertBanner>
        </div>
      ) : loading ? (
        <div style={empty}>Loading members…</div>
      ) : members.length === 0 ? (
        <div style={empty}>No members yet.{canMutate && <> Use <strong>Add member</strong> to invite someone.</>}</div>
      ) : (
        <Table cols={cols}>
          {members.map((m) => {
            const displayName = m.name || m.full_name || m.email || m.id;
            const isAdminMember = m.is_admin || m.role === 'admin' || m.role === 'super_admin';
            const canManage = m.role !== 'super_admin' || isSuperAdmin;
            const isEditing = editingId === m.id;
            return (
              <Row key={m.id}>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600 }}>{displayName}</span>
                    {isAdminMember && <Pill bg="#EEF2FF" fg="#4338CA">Admin</Pill>}
                  </div>
                  {m.email && m.email !== displayName && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{m.email}</div>}
                </Td>
                <Td>
                  {isEditing && canMutate ? (
                    <select value={editRole} onChange={(e) => setEditRole(e.target.value)} style={{ ...fieldStyle, fontSize: 12 }} aria-label="Change role">
                      {roleOptions.map(([r, label]) => <option key={r} value={r}>{label}</option>)}
                    </select>
                  ) : (
                    <span style={{ textTransform: 'capitalize' }}>{m.role}</span>
                  )}
                </Td>
                <Td>
                  {m.degraded ? (
                    <span style={{ color: 'var(--text-muted)' }} title="Auth details temporarily unavailable">—</span>
                  ) : m.mfa_enabled ? (
                    <span style={{ color: '#16A34A', fontWeight: 600, fontSize: 12 }}>On</span>
                  ) : (
                    <span style={{ color: '#D97706', fontSize: 12 }}>Off</span>
                  )}
                </Td>
                <Td>
                  {m.is_active === false
                    ? <Pill bg="#FEE2E2" fg="#DC2626">Inactive</Pill>
                    : <Pill bg="#DCFCE7" fg="#15803D">Active</Pill>}
                </Td>
                <Td style={{ color: 'var(--text-secondary)' }}>{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</Td>
                {canMutate && (
                  <Td align="right">
                    {canManage && (
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {isEditing ? (
                          <>
                            <Btn sm icon="check" accent={accent} onClick={() => saveRole(m.id)} title="Save role">Save</Btn>
                            <Btn sm icon="x" onClick={() => setEditingId(null)} title="Cancel">Cancel</Btn>
                          </>
                        ) : (
                          <>
                            <Btn sm icon="pencil" onClick={() => { setEditingId(m.id); setEditRole(m.role); }} title="Change member role">Role</Btn>
                            <Btn sm icon="trash-2" onClick={() => setRemoveTarget({ id: m.id, name: displayName })} title="Remove member" style={{ color: '#DC2626', borderColor: '#FECACA', background: '#FEF2F2' }}>Remove</Btn>
                          </>
                        )}
                      </div>
                    )}
                  </Td>
                )}
              </Row>
            );
          })}
        </Table>
      )}

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove member?"
        body={removeTarget ? `Remove ${removeTarget.name} from this account? Their account is deactivated and detached from the org.` : ''}
        confirmLabel="Remove"
        danger
        onConfirm={() => {
          if (removeTarget) remove(removeTarget.id);
          setRemoveTarget(null);
        }}
        onCancel={() => setRemoveTarget(null)}
      />
    </Card>
  );
}

export default OrgMembersCard;
